import { useCallback } from 'react';
import { useDataCache } from '../../contexts/BreakdownDataCacheContext';
import {
  upsertCalDate,
  deleteCalDate,
  shiftCalDatesDate,
  updateCalDateSoltOrder,
  updateCalDateSlotsSort as updateCalDateSlotsSortApi,
  getCalDatesByScheduleId,
} from '../../api/schedule';
import { CalDateInfo, SlotType } from '../../types/schedule.types';
import { normalizeToSortedIds } from '../../utils/calDateSlotsSortUtils';

/**
 * CalDate 更新 Hook 配置选项
 */
export interface UseCalDateUpdatesOptions {
  onSuccess?: (action: string, data: any) => void;
  onError?: (error: Error, action: string, data: any) => void;
}

/**
 * CalDate 更新 Hook
 * 提供统一的 CalDate 数据更新方法，包括：
 * - 更新/创建 CalDate（upsert）
 * - 更新 CalDate 的 calDateShift
 * - 更新 CalDate 的 calDateSlotsSort
 * - 更新 CalDate 中任务的顺序
 * - 批量移动 CalDate 日期
 * - 删除 CalDate
 * 
 * 更新流程：
 * 1. 乐观更新缓存
 * 2. 通知本标签页订阅者（UI 立即更新）
 * 3. 调用 API
 * 4. 成功：通过 BroadcastChannel 通知其他标签页
 * 5. 失败：回滚缓存，通知订阅者
 */
function sortIdsHasNonNoteSolt(
  sortIds: string[],
  scheduleId: string,
  getSlotCache: (lookupScheduleId: string) => { slots?: { slotID: string; slotType?: SlotType }[] } | null,
): boolean {
  if (sortIds.length === 0) return false;
  const cache = getSlotCache(scheduleId);
  const slots = cache?.slots ?? [];
  const map = new Map(slots.map((t) => [t.slotID, t]));
  return sortIds.some((id) => {
    const t = map.get(id);
    return t && (t.slotType === SlotType.SHOOT || !t.slotType);
  });
}

export function useCalDateUpdates(options?: UseCalDateUpdatesOptions) {
  const { getCalDateCache, setCalDateCache, getSlotCache } = useDataCache();

  const syncServerCalDate = useCallback((scheduleId: string, serverCalDate: CalDateInfo) => {
    const cache = getCalDateCache(scheduleId);
    if (!cache) return;
    const serverDateStr = serverCalDate.calDateDt?.split('T')[0];
    const existingIndex = cache.calDates.findIndex((calDate) =>
      calDate.calDateID === serverCalDate.calDateID ||
      (!!serverDateStr && calDate.calDateDt?.split('T')[0] === serverDateStr)
    );
    setCalDateCache(scheduleId, {
      calDates: existingIndex >= 0
        ? cache.calDates.map((calDate, index) => index === existingIndex ? serverCalDate : calDate)
        : [...cache.calDates, serverCalDate],
    });
  }, [getCalDateCache, setCalDateCache]);

  /**
   * Upsert CalDate（更新或创建）
   */
  const upsertCalDateData = useCallback(
    async (scheduleId: string, data: Partial<CalDateInfo>) => {
      let oldCache: any = null;
      let oldCalDates: any[] = [];

      // 当传入 calDateSlotsSort 且非 NOTE 的 slot 已全部清空时，清空 calDateShift（乐观更新与后端一致）
      if (data.calDateSlotsSort !== undefined) {
        const sortIds = normalizeToSortedIds(data.calDateSlotsSort);
        if (!sortIdsHasNonNoteSolt(sortIds, scheduleId, getSlotCache)) {
          data = { ...data, calDateShift: '' };
        }
        data = { ...data, calDateSlotsSort: sortIds };
      }

      try {
        // 乐观更新缓存
        const cache = getCalDateCache(scheduleId);
        if (cache) {
          oldCache = { ...cache };
          oldCalDates = [...cache.calDates];

          // 查找是否已存在该日期的 calDate
          const dateStr = data.calDateDt?.split('T')[0];
          const existingIndex = cache.calDates.findIndex((pd) => {
            if (!pd.calDateDt || !dateStr) return false;
            return pd.calDateDt.split('T')[0] === dateStr;
          });

          let updatedCalDates: CalDateInfo[];
          if (existingIndex >= 0) {
            // 更新现有的 calDate
            updatedCalDates = cache.calDates.map((pd, index) =>
              index === existingIndex ? { ...pd, ...data } : pd
            );
          } else {
            // 创建新的 calDate
            const newCalDate = {
              calDateID: `temp-${Date.now()}`, // 临时 ID
              ...data,
            } as CalDateInfo;
            updatedCalDates = [...cache.calDates, newCalDate];
          }

          setCalDateCache(scheduleId, {
            calDates: updatedCalDates,
          });
        }

        // 调用 API
        const response = await upsertCalDate(data);
        syncServerCalDate(scheduleId, response.data);

        // 成功回调
        if (options?.onSuccess) {
          options.onSuccess('upsert', { data });
        }
      } catch (error) {
        console.error('[useCalDateUpdates] Upsert CalDate 失败:', error);

        // 回滚缓存
        if (oldCache) {
          setCalDateCache(scheduleId, {
            calDates: oldCalDates,
          });
        }

        // 错误回调
        if (options?.onError) {
          options.onError(error as Error, 'upsert', { data });
        }

        throw error;
      }
    },
    [getCalDateCache, setCalDateCache, getSlotCache, syncServerCalDate, options]
  );

  /**
   * 更新 CalDate 的 calDateShift
   */
  const updateCalDateShift = useCallback(
    async (scheduleId: string, calDateId: string, dateStr: string, shift: string) => {
      let oldCache: any = null;
      let oldCalDates: any[] = [];

      try {
        // 乐观更新缓存
        const cache = getCalDateCache(scheduleId);
        if (cache) {
          oldCache = { ...cache };
          oldCalDates = [...cache.calDates];

          const updatedCalDates = cache.calDates.map((pd) => {
            if (pd.calDateID === calDateId || pd.calDateDt?.split('T')[0] === dateStr) {
              return { ...pd, calDateShift: shift };
            }
            return pd;
          });

          setCalDateCache(scheduleId, {
            calDates: updatedCalDates,
          });
        }

        // 调用 API
        const response = await upsertCalDate({
          calDateDt: dateStr,
          calDateBelongScheduleID: scheduleId,
          calDateShift: shift,
        });
        syncServerCalDate(scheduleId, response.data);

        // 成功回调
        if (options?.onSuccess) {
          options.onSuccess('updateShift', { calDateId, dateStr, shift });
        }
      } catch (error) {
        console.error('[useCalDateUpdates] 更新 CalDate Shift 失败:', error);

        // 回滚缓存
        if (oldCache) {
          setCalDateCache(scheduleId, {
            calDates: oldCalDates,
          });
        }

        // 错误回调
        if (options?.onError) {
          options.onError(error as Error, 'updateShift', { calDateId, dateStr, shift });
        }

        throw error;
      }
    },
    [getCalDateCache, setCalDateCache, syncServerCalDate, options]
  );

  /**
   * 更新 CalDate 的 calDateSlotsSort（slotID[]，兼容旧分组 JSON）
   */
  const updateCalDateSlotsSort = useCallback(
    async (
      scheduleId: string,
      calDateId: string,
      dateStr: string,
      sortArray: string[],
    ) => {
      let oldCache: any = null;
      let oldCalDates: any[] = [];

      const sortIds = normalizeToSortedIds(sortArray);

      try {
        const cache = getCalDateCache(scheduleId);
        if (cache) {
          oldCache = { ...cache };
          oldCalDates = [...cache.calDates];

          const clearShift = !sortIdsHasNonNoteSolt(sortIds, scheduleId, getSlotCache)
            ? { calDateShift: '' as const }
            : {};

          const updatedCalDates = cache.calDates.map((pd) => {
            if (pd.calDateID === calDateId || pd.calDateDt?.split('T')[0] === dateStr) {
              return { ...pd, calDateSlotsSort: sortIds, ...clearShift };
            }
            return pd;
          });

          setCalDateCache(scheduleId, {
            calDates: updatedCalDates,
          });
        }

        const response = await upsertCalDate({
          calDateDt: dateStr,
          calDateBelongScheduleID: scheduleId,
          calDateSlotsSort: sortIds,
        });
        syncServerCalDate(scheduleId, response.data);

        if (options?.onSuccess) {
          options.onSuccess('updateSort', { calDateId, dateStr, sortArray: sortIds });
        }
      } catch (error) {
        console.error('[useCalDateUpdates] 更新 CalDate SlotsSort 失败:', error);

        if (oldCache) {
          setCalDateCache(scheduleId, {
            calDates: oldCalDates,
          });
        }

        if (options?.onError) {
          options.onError(error as Error, 'updateSort', { calDateId, dateStr, sortArray: sortIds });
        }

        throw error;
      }
    },
    [getCalDateCache, setCalDateCache, getSlotCache, syncServerCalDate, options]
  );

  /**
   * 更新 CalDate 中任务的顺序
   */
  const updateCalDateSoltOrderData = useCallback(
    async (scheduleId: string, calDateId: string, slotId: string, targetIndex: number) => {
      let oldCache: any = null;
      let oldCalDates: any[] = [];

      try {
        // 乐观更新缓存
        const cache = getCalDateCache(scheduleId);
        if (cache) {
          oldCache = { ...cache };
          oldCalDates = [...cache.calDates];

          const updatedCalDates = cache.calDates.map((pd) => {
            if (pd.calDateID === calDateId) {
              const flat = normalizeToSortedIds(pd.calDateSlotsSort);
              const oldIndex = flat.indexOf(slotId);
              if (oldIndex === -1) return pd;
              const next = flat.filter((id) => id !== slotId);
              let adjusted = oldIndex < targetIndex ? targetIndex - 1 : targetIndex;
              adjusted = Math.max(0, Math.min(adjusted, next.length));
              next.splice(adjusted, 0, slotId);
              return { ...pd, calDateSlotsSort: next };
            }
            return pd;
          });

          setCalDateCache(scheduleId, {
            calDates: updatedCalDates,
          });
        }

        // 调用 API（只需要 3 个参数）
        const response = await updateCalDateSoltOrder(calDateId, slotId, targetIndex);
        syncServerCalDate(scheduleId, response.data);

        // 成功回调
        if (options?.onSuccess) {
          options.onSuccess('updateSoltOrder', { calDateId, slotId, targetIndex });
        }
      } catch (error) {
        console.error('[useCalDateUpdates] 更新任务顺序失败:', error);

        // 回滚缓存
        if (oldCache) {
          setCalDateCache(scheduleId, {
            calDates: oldCalDates,
          });
        }

        // 错误回调
        if (options?.onError) {
          options.onError(error as Error, 'updateSoltOrder', {
            calDateId,
            slotId,
            targetIndex,
          });
        }

        throw error;
      }
    },
    [getCalDateCache, setCalDateCache, syncServerCalDate, options]
  );

  /**
   * 直接更新 CalDate 的 calDateSlotsSort（PATCH …/slots-sort）
   */
  const updateCalDateSlotsSortData = useCallback(
    async (scheduleId: string, calDateId: string, sortIds: string[]) => {
      let oldCache: any = null;
      let oldCalDates: any[] = [];

      const normalized = normalizeToSortedIds(sortIds);
      const clearShift = !sortIdsHasNonNoteSolt(normalized, scheduleId, getSlotCache)
        ? { calDateShift: '' }
        : {};

      try {
        const cache = getCalDateCache(scheduleId);
        if (cache) {
          oldCache = { ...cache };
          oldCalDates = [...cache.calDates];
          const updatedCalDates = cache.calDates.map((pd) =>
            pd.calDateID === calDateId ? { ...pd, calDateSlotsSort: normalized, ...clearShift } : pd
          );
          setCalDateCache(scheduleId, { calDates: updatedCalDates });
        }

        const response = await updateCalDateSlotsSortApi(calDateId, normalized);
        syncServerCalDate(scheduleId, response.data);

        if (options?.onSuccess) {
          options.onSuccess('updateCalDateSlotsSort', { calDateId, sortIds: normalized });
        }
      } catch (error) {
        console.error('[useCalDateUpdates] 更新 calDateSlotsSort 失败:', error);
        if (oldCache) {
          setCalDateCache(scheduleId, { calDates: oldCalDates });
        }
        if (options?.onError) {
          options.onError(error as Error, 'updateCalDateSlotsSort', {
            calDateId,
            sortIds: normalized,
          });
        }
        throw error;
      }
    },
    [getCalDateCache, setCalDateCache, getSlotCache, syncServerCalDate, options]
  );

  /**
   * 批量移动 CalDate 日期
   * @param scheduleId 拍摄计划 ID（即 ScheduleInfo.scheduleID）
   * @param calDateIds 计划日ID数组
   * @param daysOffset 日期偏移天数
   * @param originalStartDate 原开机日期（可选，只在修改开机日期时传递）
   */
  const shiftCalDatesDateData = useCallback(
    async (
      scheduleId: string, 
      calDateIds: string[], 
      daysOffset: number,
      originalStartDate?: string | Date | null
    ) => {
      let oldCache: any = null;
      let oldCalDates: any[] = [];

      try {
        const calDateIdSet = new Set(calDateIds);

        // 乐观更新缓存（移动日期）
        // 注意：如果提供了 originalStartDate，乐观更新可能不准确，因为会跳过一些 CalDate
        // 但为了保持响应性，仍然进行乐观更新，实际结果由后端决定
        const cache = getCalDateCache(scheduleId);
        if (cache) {
          oldCache = { ...cache };
          oldCalDates = [...cache.calDates];

          // 如果有原开机日期，只更新原开机日及之后的 CalDate
          let shouldUpdate = (pd: any) => {
            if (!originalStartDate) return true;
            if (!pd.calDateDt) return false;
            
            const pdDate = new Date(pd.calDateDt);
            const originalDate = originalStartDate instanceof Date 
              ? originalStartDate 
              : new Date(originalStartDate);
            
            // 只处理原开机日及之后的 CalDate
            pdDate.setHours(0, 0, 0, 0);
            originalDate.setHours(0, 0, 0, 0);
            
            return pdDate >= originalDate;
          };

          const updatedCalDates = cache.calDates.map((pd) => {
            if (calDateIdSet.has(pd.calDateID) && shouldUpdate(pd)) {
              // 计算新日期
              const oldDate = pd.calDateDt ? new Date(pd.calDateDt) : null;

              if (oldDate) {
                const newDate = new Date(oldDate);
                newDate.setDate(newDate.getDate() + daysOffset);
                const newDateStr = newDate.toISOString().split('T')[0];

                return { ...pd, calDateDt: newDateStr };
              }
            }
            return pd;
          });

          setCalDateCache(scheduleId, {
            calDates: updatedCalDates,
          });
        }

        // 调用 API
        await shiftCalDatesDate(calDateIds, daysOffset, originalStartDate);

        // 从服务器重新获取最新的 CalDate 数据，确保获取到合并、删除后的最新状态
        try {
          const response = await getCalDatesByScheduleId(scheduleId);
          const latestCalDates = response.data || [];
          
          console.log('[useCalDateUpdates] 重新获取 CalDate 数据:', latestCalDates.length, '条');
          
          // 更新缓存为服务器返回的最新数据
          setCalDateCache(scheduleId, {
            calDates: latestCalDates,
          });
        } catch (refreshError) {
          console.error('[useCalDateUpdates] 重新获取 CalDate 数据失败:', refreshError);
          // 如果重新获取失败，保持乐观更新的结果，不抛出错误
        }

        // 成功回调
        if (options?.onSuccess) {
          options.onSuccess('shift', { calDateIds, daysOffset, originalStartDate });
        }
      } catch (error) {
        console.error('[useCalDateUpdates] 批量移动 CalDate 日期失败:', error);

        // 回滚缓存
        if (oldCache) {
          setCalDateCache(scheduleId, {
            calDates: oldCalDates,
          });
        }

        // 错误回调
        if (options?.onError) {
          options.onError(error as Error, 'shift', { calDateIds, daysOffset, originalStartDate });
        }

        throw error;
      }
    },
    [getCalDateCache, setCalDateCache, options]
  );

  /**
   * 删除 CalDate
   */
  const deleteCalDateData = useCallback(
    async (scheduleId: string, calDateId: string) => {
      let oldCache: any = null;
      let oldCalDates: any[] = [];

      try {
        // 乐观删除缓存
        const cache = getCalDateCache(scheduleId);
        if (cache) {
          oldCache = { ...cache };
          oldCalDates = [...cache.calDates];

          const updatedCalDates = cache.calDates.filter((pd) => pd.calDateID !== calDateId);

          setCalDateCache(scheduleId, {
            calDates: updatedCalDates,
          });
        }

        // 调用 API
        await deleteCalDate(calDateId);

        // 成功回调
        if (options?.onSuccess) {
          options.onSuccess('delete', { calDateId });
        }
      } catch (error) {
        console.error('[useCalDateUpdates] 删除 CalDate 失败:', error);

        // 回滚缓存
        if (oldCache) {
          setCalDateCache(scheduleId, {
            calDates: oldCalDates,
          });
        }

        // 错误回调
        if (options?.onError) {
          options.onError(error as Error, 'delete', { calDateId });
        }

        throw error;
      }
    },
    [getCalDateCache, setCalDateCache, options]
  );

  return {
    upsertCalDateData,
    updateCalDateShift,
    updateCalDateSlotsSort,
    updateCalDateSlotsSortData,
    updateCalDateSoltOrderData,
    shiftCalDatesDateData,
    deleteCalDateData,
  };
}
